import type { CorpusAnalysisResult } from '../api/corpusAnalysisAPI'
import type { StagedReferenceAlignmentClusterIntake } from '../api/stagedReferenceAlignmentAPI'

type ClusterSummary = StagedReferenceAlignmentClusterIntake['clusters'][number]

const sortClusterEntries = (entries: Array<[string, string[]]>): Array<[string, string[]]> => (
  [...entries].sort(([left], [right]) => {
    const leftNumeric = Number.parseInt(left, 10)
    const rightNumeric = Number.parseInt(right, 10)
    // mutation-ignore-next-line equivalent: numeric-looking object keys are already enumerated first.
    if (Number.isNaN(leftNumeric) && Number.isNaN(rightNumeric)) {
      return left.localeCompare(right)
    }
    if (Number.isNaN(leftNumeric)) {
      return 1
    }
    if (Number.isNaN(rightNumeric)) {
      return -1
    }
    return leftNumeric - rightNumeric
  })
)

export const buildWorkbenchClusterIntake = ({
  analysisData,
  analysisWitnessSigla,
  selectedWitnessSigla,
}: {
  analysisData: CorpusAnalysisResult
  analysisWitnessSigla: string[]
  selectedWitnessSigla: string[]
}): StagedReferenceAlignmentClusterIntake => {
  const selectedWitnessIdSet = new Set(selectedWitnessSigla.map(siglum => siglum.trim()).filter(Boolean))

  const clusters = analysisData.progressive_plan?.clusters?.length
    ? analysisData.progressive_plan.clusters
        .map(cluster => {
          const orderedMembers = (cluster.order?.length ? cluster.order : analysisData.clusters?.[cluster.cluster_id] || [])
            .map(siglum => siglum.trim())
            .filter(Boolean)
          const selectedMembers = orderedMembers.filter(siglum => selectedWitnessIdSet.has(siglum))
          if (selectedMembers.length === 0) {
            return null
          }
          return {
            cluster_id: cluster.cluster_id,
            cluster_size: cluster.size || orderedMembers.length,
            ground_truth: cluster.ground_truth || orderedMembers[0] || selectedMembers[0],
            ordered_members: orderedMembers,
            selected_members: selectedMembers,
          }
        })
        .filter((cluster): cluster is ClusterSummary => Boolean(cluster))
    : sortClusterEntries(Object.entries(analysisData.clusters || {}))
        .map(([clusterId, members]) => {
          const orderedMembers = members.map(siglum => siglum.trim()).filter(Boolean)
          const selectedMembers = orderedMembers.filter(siglum => selectedWitnessIdSet.has(siglum))
          if (selectedMembers.length === 0) {
            return null
          }
          return {
            cluster_id: clusterId,
            cluster_size: orderedMembers.length,
            ground_truth: orderedMembers[0],
            ordered_members: orderedMembers,
            selected_members: selectedMembers,
          }
        })
        .filter((cluster): cluster is ClusterSummary => Boolean(cluster))

  return {
    analysis_witness_sigla: analysisWitnessSigla,
    selected_witness_sigla: selectedWitnessSigla,
    clusters,
    recommended_ground_truth: analysisData.recommended_ground_truth,
    recommendation_reason: analysisData.recommendation_reason,
  }
}
